import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, InputAdornment, MenuItem, Paper, Stack, Switch, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import {
    EditorProvider, Editor as SimpleWysiwygEditor, Toolbar,
    BtnBold, BtnItalic, BtnUnderline, BtnStrikeThrough, BtnLink, BtnNumberedList, BtnBulletList, BtnUndo, BtnRedo, Separator,
} from 'react-simple-wysiwyg';
import {
    DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
    closestCorners, useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext, useSortable, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ConfigCard } from '../style/cards';
import { useWebshopLanguages } from '../WebshopLanguagesContext';

const ALL_LANGS = [
    { code: 'nl', label: 'NL', long: 'Dutch'   },
    { code: 'en', label: 'GB', long: 'English' },
    { code: 'fr', label: 'FR', long: 'French'  },
];

// Product fields the storefront's look-detail page understands — the same
// token vocabulary as the product detail page (SHOW_FIELDS_ON_DETAIL_*). Title
// and subtitle pull from this pool; `category_N` labels are overridden by the
// shop-specific kasvars labels when present. Stored space-separated in
// SHOW_FIELDS_ON_LOOK_TITLE / _SUBHEADING.
const CHIP_FIELDS = [
    { key: 'brand',       label: 'Supplier' },
    { key: 'name',        label: 'Name' },
    { key: 'reference',   label: 'Reference' },
    { key: 'color',       label: 'Color' },
    { key: 'color_brand', label: 'Variant' },
    { key: 'season',      label: 'Season' },
    ...[1, 2, 3, 4, 5, 6, 7].map(n => ({ key: `category_${n}`, label: `Category ${n}` })),
    ...[1, 2, 3, 4, 5].map(n => ({ key: `description_${n}`, label: `Description ${n}` })),
    ...[1, 2, 3, 4, 5].map(n => ({ key: `detail_${n}`, label: `Details ${n}` })),
];

// `looksGrid.showInIframe` ↔ the TEXT_LOOKS content row's use_iframe flag.
// `overlay.ctaText` is the shared i18n translation GLOBAL.LOOKS_DETAILS — it's
// per-language and saved via the localization route, not a per-shop setting.
export const STL_CONFIG_DEFAULT = {
    introText: { nl: '', en: '', fr: '' },
    introEditor: 'visual',  // 'visual' | 'html' — UI only
    looksGrid: {
        navigationCategory: '1',       // LOOKS_NAVIGATION_CODE — bare category index
        displayOrder: 'SORT_DEFAULT',  // LOOKS_LIST_SORTING
        looksPerRow: 3,                // MAX_COLUMNS_LOOKS
        showInIframe: false,           // pending backend
    },
    overlay: {
        onlyOnHover: false,
        bandBg: 'rgba(0, 0, 0, 0.1)',
        bandHeightPct: 100,
        bandVerticalPosition: 'middle',
        showTitle: true,
        titleColor: '#ffffff',
        titleFontSize: 12,
        showCta: true,
        ctaText: { nl: 'Bewonder', en: 'View look', fr: 'Voir le look' }, // i18n GLOBAL.LOOKS_DETAILS
        ctaBg: '#000000',
        ctaTextColor: '#ffffff',
        ctaFontSize: 12,
    },
    detailPage: {
        descriptionPosition: 'top',    // LOOKS_DETAIL_TEXT_LOCATION: 'top' | 'photo'
        showDividerBetweenProducts: false,
        showOutOfStock: false,
    },
    productTitle:    [],   // chip order = display order (space-separated tokens)
    productSubtitle: [],
};

const ShopTheLookTab = ({ theme, t, config: configProp, onConfigChange, shopFieldLabels = {} }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);
    // Only show language tabs the admin is allowed to edit (ADMIN_LANGUAGES).
    const { adminLanguages } = useWebshopLanguages();
    const LANGS = ALL_LANGS.filter(l => adminLanguages.includes(String(l.code).toUpperCase()));
    const [langTab, setLangTab] = useState(0);
    const activeLangIdx = (langTab >= 0 && langTab < LANGS.length) ? langTab : 0;
    const lang = (LANGS[activeLangIdx] || ALL_LANGS[0]).code;

    // Controlled by the parent (loaded from LOOKS_* settings + TEXT_LOOKS,
    // saved on the global Save) when wired; local fallback otherwise.
    const controlled = configProp != null && typeof onConfigChange === 'function';
    const [localConfig, setLocalConfig] = useState(STL_CONFIG_DEFAULT);
    const config = controlled ? configProp : localConfig;
    const setConfig = controlled ? onConfigChange : setLocalConfig;

    // Label for a chip key — category_N uses the shop's own kasvars label.
    const chipLabel = (key) => {
        if (shopFieldLabels[key]) return shopFieldLabels[key];
        const def = CHIP_FIELDS.find(f => f.key === key);
        return def ? def.label : key;
    };

    const setIntroText = (val) => setConfig(prev => ({
        ...prev,
        introText: { ...prev.introText, [lang]: val },
    }));
    const setSection = (sec) => (patch) => setConfig(prev => ({
        ...prev,
        [sec]: { ...prev[sec], ...patch },
    }));
    const setOverlay  = setSection('overlay');
    const setLooks    = setSection('looksGrid');
    const setDetail   = setSection('detailPage');

    const copyIntroFrom = (fromLang) => {
        if (fromLang === lang) return;
        setConfig(prev => ({
            ...prev,
            introText: { ...prev.introText, [lang]: prev.introText[fromLang] || '' },
        }));
    };

    // Chip helpers: title and subtitle are ordered arrays of field-keys; the
    // remaining fields make up the "available" pool. A field lives in exactly
    // one zone, so its key doubles as its drag id.
    const used = new Set([...config.productTitle, ...config.productSubtitle]);
    const availableKeys = CHIP_FIELDS.map(f => f.key).filter(k => !used.has(k));
    const addToTitle    = (key) => setConfig(prev => ({ ...prev, productTitle:    [...prev.productTitle.filter(k => k !== key),    key], productSubtitle: prev.productSubtitle.filter(k => k !== key) }));
    const removeFrom    = (which) => (key) => setConfig(prev => ({ ...prev, [which]: prev[which].filter(k => k !== key) }));

    // ── Drag & drop across the three zones (title / subtitle / available) ──
    const [dragKey, setDragKey] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const zoneOf = (key) => (config.productTitle.includes(key) ? 'title' : config.productSubtitle.includes(key) ? 'subtitle' : 'available');
    // Move `key` into `toZone` at the slot of `overKey` (or end). "available"
    // is derived, so landing there just means removing it from both arrays.
    const moveField = (key, toZone, overKey) => setConfig(prev => {
        const title    = prev.productTitle.filter(k => k !== key);
        const subtitle = prev.productSubtitle.filter(k => k !== key);
        const insertInto = (arr) => {
            const idx = overKey && arr.includes(overKey) ? arr.indexOf(overKey) : arr.length;
            const next = [...arr];
            next.splice(idx, 0, key);
            return next;
        };
        if (toZone === 'title')    return { ...prev, productTitle: insertInto(title), productSubtitle: subtitle };
        if (toZone === 'subtitle') return { ...prev, productTitle: title, productSubtitle: insertInto(subtitle) };
        return { ...prev, productTitle: title, productSubtitle: subtitle }; // available
    });
    const handleDragEnd = ({ active, over }) => {
        setDragKey(null);
        if (!over) return;
        const key = active.id;
        // over.id is either another chip's key or a zone id ('z-title' etc.)
        const overId = over.id;
        const toZone = String(overId).startsWith('z-') ? String(overId).slice(2) : zoneOf(overId);
        const overKey = String(overId).startsWith('z-') ? null : overId;
        if (key === overKey) return;
        moveField(key, toZone, overKey);
    };

    return (
        <Stack spacing={2}>
            {/* ─── Intro text ─── */}
            <ConfigCard
                anchor="cfg-card-stl-intro"
                title={tr('cfg_stl_intro_title', 'Intro text')}
                description={tr('cfg_stl_intro_desc', 'A welcome message shown above the looks grid. Each language has its own version.')}
                headerActions={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {tr('cfg_stl_intro_copy_from', 'Copy layout from:')}
                        </Typography>
                        {LANGS.filter((_, i) => i !== activeLangIdx).map((l) => (
                            <Button key={l.code} size="small" variant="outlined" onClick={() => copyIntroFrom(l.code)} sx={{ textTransform: 'none', minWidth: 0, px: 1.25 }}>
                                {l.long}
                            </Button>
                        ))}
                    </Box>
                }
                theme={theme}
            >
                <Tabs
                    value={activeLangIdx}
                    onChange={(_e, v) => setLangTab(v)}
                    sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, px: 1.5, py: 0.5, fontSize: '0.8rem', textTransform: 'none' } }}
                >
                    {LANGS.map((l) => (
                        <Tab key={l.code} label={<Box component="span"><Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>{l.label}</Box>{l.long}</Box>} />
                    ))}
                </Tabs>

                {config.introEditor === 'visual' ? (
                    <Box sx={{
                        border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
                        '& .rsw-editor': { minHeight: 180 },
                        '& .rsw-ce':     { minHeight: 140, padding: '12px 16px' },
                    }}>
                        <EditorProvider>
                            <SimpleWysiwygEditor
                                value={config.introText[lang] || ''}
                                onChange={(e) => setIntroText(e.target.value)}
                            >
                                <Toolbar>
                                    <BtnUndo /><BtnRedo /><Separator />
                                    <BtnBold /><BtnItalic /><BtnUnderline /><BtnStrikeThrough /><Separator />
                                    <BtnNumberedList /><BtnBulletList /><Separator />
                                    <BtnLink />
                                </Toolbar>
                            </SimpleWysiwygEditor>
                        </EditorProvider>
                    </Box>
                ) : (
                    <TextField
                        multiline minRows={6}
                        value={config.introText[lang] || ''}
                        onChange={(e) => setIntroText(e.target.value)}
                        fullWidth
                        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                        placeholder="<p>Laat je inspireren door onze herfst- &amp; winterlooks!</p>"
                    />
                )}

                {/* Editor mode toggle — two big buttons across the bottom */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Button
                        size="small"
                        variant={config.introEditor === 'visual' ? 'contained' : 'outlined'}
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => setConfig(prev => ({ ...prev, introEditor: 'visual' }))}
                        sx={{ textTransform: 'none', py: 1 }}
                    >
                        {tr('cfg_stl_intro_visual', 'Visual editor')}
                    </Button>
                    <Button
                        size="small"
                        variant={config.introEditor === 'html' ? 'contained' : 'outlined'}
                        startIcon={<CodeIcon fontSize="small" />}
                        onClick={() => setConfig(prev => ({ ...prev, introEditor: 'html' }))}
                        sx={{ textTransform: 'none', py: 1 }}
                    >
                        {tr('cfg_stl_intro_html', 'HTML editor')}
                    </Button>
                </Box>
            </ConfigCard>

            {/* ─── Looks grid ─── */}
            <ConfigCard anchor="cfg-card-stl-grid" title={tr('cfg_stl_grid_title', 'Looks grid')} description={tr('cfg_stl_grid_desc', 'How the looks are arranged on the overview page.')} theme={theme}>
                <SettingRow
                    label={tr('cfg_stl_grid_nav_category', 'Category used for navigation')}
                    help={tr('cfg_stl_grid_nav_category_help', 'When visitors filter looks, this is the category tree used for navigation')}
                    control={
                        <TextField
                            select size="small"
                            value={config.looksGrid.navigationCategory}
                            onChange={(e) => setLooks({ navigationCategory: e.target.value })}
                            sx={{ minWidth: 200 }}
                        >
                            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                                <MenuItem key={n} value={String(n)}>
                                    {shopFieldLabels[`category_${n}`] || `${tr('cfg_stl_category_prefix', 'Category')} ${n}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    }
                />
                <SettingRow
                    label={tr('cfg_stl_grid_order', 'Display order')}
                    help={tr('cfg_stl_grid_order_help', 'How looks are sorted on the overview page')}
                    control={
                        <TextField
                            select size="small"
                            value={config.looksGrid.displayOrder}
                            onChange={(e) => setLooks({ displayOrder: e.target.value })}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="SORT_DEFAULT">{tr('cfg_stl_order_default', 'Default')}</MenuItem>
                            <MenuItem value="SORT_ASCENDING">{tr('cfg_stl_order_ascending', 'Alphabetical (ascending)')}</MenuItem>
                            <MenuItem value="SORT_DESCENDING">{tr('cfg_stl_order_descending', 'Alphabetical (descending)')}</MenuItem>
                        </TextField>
                    }
                />
                <SettingRow
                    label={tr('cfg_stl_grid_looks_per_row', 'Looks per row')}
                    help={tr('cfg_stl_grid_looks_per_row_help', 'How many looks appear side by side in the grid')}
                    control={
                        <TextField
                            type="number" size="small"
                            value={config.looksGrid.looksPerRow}
                            onChange={(e) => setLooks({ looksPerRow: Math.max(1, parseInt(e.target.value) || 1) })}
                            InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_stl_looks_unit', 'looks')}</InputAdornment> }}
                            sx={{ width: 130 }}
                        />
                    }
                />
                <SettingRow
                    label={tr('cfg_stl_grid_iframe', 'Show in iframe')}
                    help={tr('cfg_stl_grid_iframe_help', 'Display the looks page inside an iframe. Recommended only when using advanced custom styling or scripts.')}
                    control={<Switch color="success" size="small" checked={config.looksGrid.showInIframe} onChange={(e) => setLooks({ showInIframe: e.target.checked })} />}
                />
            </ConfigCard>

            {/* ─── Look card overlay ─── */}
            <ConfigCard anchor="cfg-card-stl-overlay" title={tr('cfg_stl_overlay_title', 'Look card overlay')} description={tr('cfg_stl_overlay_desc', 'The colored band shown over each look card with the look name and a call-to-action button.')} theme={theme}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 280px' }, gap: 2 }}>
                    {/* Left: settings */}
                    <Stack spacing={1.5}>
                        <SettingRow
                            label={tr('cfg_stl_overlay_hover_only', 'Show overlay only on hover (desktop)')}
                            help={tr('cfg_stl_overlay_hover_only_help', "When on, the overlay band only appears when a visitor hovers a look. When off, it's always visible.")}
                            control={<Switch color="success" size="small" checked={config.overlay.onlyOnHover} onChange={(e) => setOverlay({ onlyOnHover: e.target.checked })} />}
                        />

                        {/* Band style block */}
                        <SubBlock label={tr('cfg_stl_overlay_band_style', 'Band style')} help={tr('cfg_stl_overlay_band_style_help', 'The colored strip across the look card')}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 1.5, alignItems: 'flex-end' }}>
                                <ColorTile label={tr('cfg_stl_overlay_band_bg', 'BACKGROUND')} value={config.overlay.bandBg}    onChange={(v) => setOverlay({ bandBg: v })} />
                                <PercentInput label={tr('cfg_stl_overlay_band_height', 'HEIGHT')} value={config.overlay.bandHeightPct} onChange={(v) => setOverlay({ bandHeightPct: v })} />
                                <SelectField  label={tr('cfg_stl_overlay_band_vpos',   'VERTICAL POSITION')} value={config.overlay.bandVerticalPosition} onChange={(v) => setOverlay({ bandVerticalPosition: v })}
                                    options={[
                                        { value: 'top',    label: tr('cfg_stl_overlay_pos_top',    'Top')    },
                                        { value: 'middle', label: tr('cfg_stl_overlay_pos_middle', 'Middle') },
                                        { value: 'bottom', label: tr('cfg_stl_overlay_pos_bottom', 'Bottom') },
                                    ]}
                                />
                            </Box>
                        </SubBlock>

                        <SettingRow
                            label={tr('cfg_stl_overlay_show_title', 'Show look title on the band')}
                            help={tr('cfg_stl_overlay_show_title_help', "Display the look's name (e.g. \"Autumn casual\") on the colored band")}
                            control={<Switch color="success" size="small" checked={config.overlay.showTitle} onChange={(e) => setOverlay({ showTitle: e.target.checked })} />}
                        />
                        {config.overlay.showTitle && (
                            <SubBlock label={tr('cfg_stl_overlay_title_style', 'Title style')}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1.5, alignItems: 'flex-end' }}>
                                    <ColorTile label={tr('cfg_stl_overlay_title_color', 'TEXT COLOR')} value={config.overlay.titleColor} onChange={(v) => setOverlay({ titleColor: v })} />
                                    <PixelInput label={tr('cfg_stl_overlay_title_size', 'FONT SIZE')} value={config.overlay.titleFontSize} onChange={(v) => setOverlay({ titleFontSize: v })} />
                                </Box>
                            </SubBlock>
                        )}

                        <SettingRow
                            label={tr('cfg_stl_overlay_show_cta', 'Show call-to-action button')}
                            help={tr('cfg_stl_overlay_show_cta_help', 'A button visitors click to open the look detail page')}
                            control={<Switch color="success" size="small" checked={config.overlay.showCta} onChange={(e) => setOverlay({ showCta: e.target.checked })} />}
                        />
                        {config.overlay.showCta && (
                            <>
                                <SubBlock label={tr('cfg_stl_overlay_button_text', 'Button text')} help={tr('cfg_stl_overlay_button_text_help', 'The label shown on every look button. This is a shared translation, so it applies to all looks — set it per language.')}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                                        {LANGS.map((l) => (
                                            <TextField
                                                key={l.code}
                                                size="small" fullWidth
                                                label={l.long}
                                                InputLabelProps={{ shrink: true }}
                                                value={(config.overlay.ctaText && config.overlay.ctaText[l.code]) || ''}
                                                onChange={(e) => setOverlay({ ctaText: { ...(config.overlay.ctaText || {}), [l.code]: e.target.value } })}
                                            />
                                        ))}
                                    </Box>
                                </SubBlock>
                                <SubBlock label={tr('cfg_stl_overlay_button_style', 'Button style')}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: 1.5, alignItems: 'flex-end' }}>
                                        <ColorTile label={tr('cfg_stl_overlay_band_bg', 'BACKGROUND')} value={config.overlay.ctaBg}        onChange={(v) => setOverlay({ ctaBg: v })} />
                                        <ColorTile label={tr('cfg_stl_overlay_title_color', 'TEXT COLOR')} value={config.overlay.ctaTextColor} onChange={(v) => setOverlay({ ctaTextColor: v })} />
                                        <PixelInput label={tr('cfg_stl_overlay_title_size', 'FONT SIZE')} value={config.overlay.ctaFontSize} onChange={(v) => setOverlay({ ctaFontSize: v })} />
                                    </Box>
                                </SubBlock>
                            </>
                        )}
                    </Stack>

                    {/* Right: live preview tile */}
                    <Box>
                        <Paper variant="outlined" sx={{
                            position: 'relative',
                            aspectRatio: '3 / 4',
                            backgroundColor: '#f5e8d8',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            overflow: 'hidden',
                            borderRadius: 1.5,
                        }}>
                            <Typography variant="caption" sx={{ position: 'absolute', top: 6, left: 8, fontSize: '0.6rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                {tr('cfg_stl_overlay_live_preview', 'LIVE PREVIEW')}
                            </Typography>
                            {/* AIGA-style restroom mannequin — outline only,
                                matches the line-drawing in the schema. One
                                continuous path for body+arms+legs avoids
                                visible internal stroke seams where parts
                                would otherwise overlap. Head is a separate
                                circle so the small skin gap between head and
                                shoulders reads clearly. 50% opacity keeps the
                                colored band overlay legible on top. */}
                            <Box sx={{
                                position: 'absolute', left: '50%', top: '6%', bottom: '5%',
                                width: '60%', transform: 'translateX(-50%)',
                                opacity: 0.5,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Box component="svg" viewBox="0 0 120 280" sx={{ width: '100%', height: '100%' }} preserveAspectRatio="xMidYMid meet">
                                    <g fill="none" stroke="#666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        {/* Head */}
                                        <circle cx="60" cy="30" r="19" />
                                        {/* Body outline traced clockwise from the
                                            left shoulder peak: over the top,
                                            down the outer right arm, around the
                                            right hand, up the inner right arm
                                            to the right armpit, down the right
                                            torso to the right hip, down the
                                            outer right leg, around the foot,
                                            up the inner right leg to the
                                            crotch, across to the inner left
                                            leg, down to the left foot, around
                                            it, up the outer left leg to the
                                            hip, up the left torso, down the
                                            inner left arm, around the left
                                            hand, up the outer left arm, close. */}
                                        <path d="
                                            M 36 58
                                            Q 60 50 84 58
                                            L 96 156
                                            Q 96 168 88 168
                                            L 80 168
                                            Q 72 168 72 156
                                            L 78 70
                                            L 78 158
                                            L 82 264
                                            Q 82 272 76 272
                                            L 70 272
                                            Q 66 272 66 264
                                            L 66 170
                                            L 54 170
                                            L 54 264
                                            Q 54 272 50 272
                                            L 42 272
                                            Q 36 272 36 264
                                            L 40 158
                                            L 42 70
                                            L 48 156
                                            Q 48 168 40 168
                                            L 32 168
                                            Q 24 168 24 156
                                            Z
                                        " />
                                    </g>
                                </Box>
                            </Box>
                            {/* Band overlay */}
                            <Box sx={{
                                position: 'absolute',
                                left: 0, right: 0,
                                top:    config.overlay.bandVerticalPosition === 'top'    ? 0   : config.overlay.bandVerticalPosition === 'middle' ? '50%' : 'auto',
                                bottom: config.overlay.bandVerticalPosition === 'bottom' ? 0   : 'auto',
                                transform: config.overlay.bandVerticalPosition === 'middle' ? 'translateY(-50%)' : 'none',
                                height: `${Math.max(8, Math.min(80, Number(config.overlay.bandHeightPct) || 0))}%`,
                                backgroundColor: config.overlay.bandBg,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                                opacity: config.overlay.onlyOnHover ? 0.7 : 1,
                            }}>
                                {config.overlay.showTitle && (
                                    <Typography sx={{
                                        color: config.overlay.titleColor,
                                        fontSize: `${Math.min(20, Number(config.overlay.titleFontSize) || 12)}px`,
                                        fontWeight: 600,
                                    }}>
                                        Autumn casual
                                    </Typography>
                                )}
                                {config.overlay.showCta && (
                                    <Box sx={{
                                        backgroundColor: config.overlay.ctaBg,
                                        color: config.overlay.ctaTextColor,
                                        px: 1.5, py: 0.5,
                                        borderRadius: 99,
                                        fontSize: `${Math.min(20, Number(config.overlay.ctaFontSize) || 12)}px`,
                                        fontWeight: 500,
                                    }}>
                                        {(config.overlay.ctaText && (config.overlay.ctaText[lang] || config.overlay.ctaText.nl)) || 'Bewonder'}
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, textAlign: 'center' }}>
                            {tr('cfg_stl_overlay_preview_caption', 'Preview updates as you change the settings on the left.')}
                        </Typography>
                    </Box>
                </Box>
            </ConfigCard>

            {/* ─── Look detail page ─── */}
            <ConfigCard anchor="cfg-card-stl-detail" title={tr('cfg_stl_detail_title', 'Look detail page')} description={tr('cfg_stl_detail_desc', 'The page a visitor sees when they open a single look.')} theme={theme}>
                <SettingRow
                    label={tr('cfg_stl_detail_desc_pos', 'Description position')}
                    help={tr('cfg_stl_detail_desc_pos_help', "Where the look's description text sits on the detail page")}
                    control={
                        <ToggleButtonGroup
                            exclusive size="small"
                            value={config.detailPage.descriptionPosition}
                            onChange={(_e, v) => v && setDetail({ descriptionPosition: v })}
                            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', flexDirection: 'column', gap: 0.5, px: 2, py: 1, minWidth: 130 } }}
                        >
                            <ToggleButton value="photo">
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
                                    <Box sx={{ width: 30, height: 18, backgroundColor: 'currentColor', opacity: 0.7, borderRadius: 0.5 }} />
                                    <Box sx={{ width: 30, height: 4,  backgroundColor: 'currentColor', opacity: 0.4, borderRadius: 0.5 }} />
                                </Box>
                                <Typography variant="caption">{tr('cfg_stl_detail_pos_below', 'Below the main photo')}</Typography>
                            </ToggleButton>
                            <ToggleButton value="top">
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
                                    <Box sx={{ width: 30, height: 4,  backgroundColor: 'currentColor', opacity: 0.4, borderRadius: 0.5 }} />
                                    <Box sx={{ width: 30, height: 18, backgroundColor: 'currentColor', opacity: 0.7, borderRadius: 0.5 }} />
                                </Box>
                                <Typography variant="caption">{tr('cfg_stl_detail_pos_top', 'Top, centered')}</Typography>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    }
                />
                <SettingRow
                    label={tr('cfg_stl_detail_show_divider', 'Show divider between products in the look')}
                    help={tr('cfg_stl_detail_show_divider_help', 'A thin separator line between each product shown on the look detail page')}
                    control={<Switch color="success" size="small" checked={config.detailPage.showDividerBetweenProducts} onChange={(e) => setDetail({ showDividerBetweenProducts: e.target.checked })} />}
                />
                <SettingRow
                    label={tr('cfg_stl_detail_show_oos', 'Show out-of-stock products')}
                    help={tr('cfg_stl_detail_show_oos_help', "Keep products in the look's product list even if they are no longer available")}
                    control={<Switch color="success" size="small" checked={config.detailPage.showOutOfStock} onChange={(e) => setDetail({ showOutOfStock: e.target.checked })} />}
                />
            </ConfigCard>

            {/* ─── Product title & subtitle (look detail page) ─── */}
            <ConfigCard anchor="cfg-card-stl-product-title" title={tr('cfg_stl_pts_title', 'Product title & subtitle (look detail page)')} description={tr('cfg_stl_pts_desc', 'How each product within a look is identified. Drag fields between the rows to reorder or move them; click an available field to add it to the title.')} theme={theme}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCorners}
                    onDragStart={({ active }) => setDragKey(active.id)}
                    onDragEnd={handleDragEnd}
                    onDragCancel={() => setDragKey(null)}
                >
                    <FieldZone
                        id="z-title" label={tr('cfg_stl_pts_title_lr', 'TITLE (LEFT TO RIGHT)')}
                        itemKeys={config.productTitle} labelFor={chipLabel} onRemove={removeFrom('productTitle')}
                        placeholder={tr('cfg_stl_pts_title_placeholder', 'Drag fields here')}
                    />
                    <FieldZone
                        id="z-subtitle" label={tr('cfg_stl_pts_subtitle_lr', 'SUBTITLE (LEFT TO RIGHT)')}
                        itemKeys={config.productSubtitle} labelFor={chipLabel} onRemove={removeFrom('productSubtitle')}
                        placeholder={tr('cfg_stl_pts_subtitle_placeholder', 'Drag fields here')}
                    />
                    <FieldZone
                        id="z-available" label={tr('cfg_stl_pts_available', 'AVAILABLE FIELDS (CLICK OR DRAG TO ADD)')}
                        itemKeys={availableKeys} labelFor={chipLabel} onAdd={addToTitle}
                        emptyLabel={tr('cfg_stl_pts_all_used', 'All fields are in use.')}
                        variant="available"
                    />
                    <DragOverlay>
                        {dragKey ? <FieldChipVisual label={chipLabel(dragKey)} dragging /> : null}
                    </DragOverlay>
                </DndContext>
            </ConfigCard>
        </Stack>
    );
};

// A single draggable field chip. The whole chip is the drag handle (via
// listeners); the × button stops propagation so removing doesn't start a drag.
// In the "available" zone there's no × and a click adds the field to the title.
const SortableFieldChip = ({ id, label, onRemove, onAdd }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
    return (
        <Box ref={setNodeRef} style={style} {...attributes} {...listeners}
            onClick={onAdd ? () => onAdd(id) : undefined}
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.25,
                pl: 0.5, pr: onRemove ? 0.25 : 1, py: 0.25, borderRadius: 1,
                backgroundColor: onAdd ? 'transparent' : 'rgba(25,118,210,0.10)',
                color: 'primary.main',
                border: onAdd ? '1px dashed' : '1px solid',
                borderColor: onAdd ? 'rgba(25,118,210,0.40)' : 'transparent',
                fontWeight: 500, fontSize: '0.8125rem',
                cursor: 'grab', userSelect: 'none', touchAction: 'none',
            }}
        >
            {onAdd
                ? <AddIcon sx={{ fontSize: 16 }} />
                : <DragIndicatorIcon sx={{ fontSize: 16, color: 'primary.main', opacity: 0.6 }} />}
            {label}
            {onRemove && (
                <CloseIcon
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onRemove(id); }}
                    sx={{ fontSize: 15, ml: 0.25, cursor: 'pointer', '&:hover': { color: '#b91c1c' } }}
                />
            )}
        </Box>
    );
};
SortableFieldChip.propTypes = { id: PropTypes.string, label: PropTypes.node, onRemove: PropTypes.func, onAdd: PropTypes.func };

// One drop zone (title / subtitle / available). Registers a droppable so empty
// zones still accept drops, and a SortableContext so chips reorder.
const FieldZone = ({ id, label, itemKeys, labelFor, onRemove, onAdd, placeholder, emptyLabel, variant }) => {
    const { setNodeRef, isOver } = useDroppable({ id });
    const available = variant === 'available';
    return (
        <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, letterSpacing: 0.5 }}>{label}</Typography>
            <SortableContext id={id} items={itemKeys} strategy={rectSortingStrategy}>
                <Paper
                    ref={setNodeRef}
                    variant="outlined"
                    sx={{
                        minHeight: 44, p: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', borderRadius: 1,
                        backgroundColor: isOver ? 'rgba(25,118,210,0.06)' : (itemKeys.length === 0 ? 'rgba(0,0,0,0.02)' : 'transparent'),
                        borderStyle: isOver ? 'dashed' : 'solid',
                        borderColor: isOver ? 'primary.main' : 'divider',
                        transition: 'background-color 0.12s, border-color 0.12s',
                    }}
                >
                    {itemKeys.length === 0 ? (
                        <Typography variant="caption" sx={{ color: 'text.disabled', pl: 0.75 }}>
                            {available ? emptyLabel : placeholder}
                        </Typography>
                    ) : (
                        itemKeys.map((key) => (
                            <SortableFieldChip
                                key={key} id={key} label={labelFor(key)}
                                onRemove={available ? undefined : onRemove}
                                onAdd={available ? onAdd : undefined}
                            />
                        ))
                    )}
                </Paper>
            </SortableContext>
        </Box>
    );
};
FieldZone.propTypes = { id: PropTypes.string, label: PropTypes.node, itemKeys: PropTypes.array, labelFor: PropTypes.func, onRemove: PropTypes.func, onAdd: PropTypes.func, placeholder: PropTypes.node, emptyLabel: PropTypes.node, variant: PropTypes.string };

// Static chip used in the DragOverlay (no sortable hooks).
const FieldChipVisual = ({ label }) => (
    <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25, pl: 0.5, pr: 1, py: 0.25, borderRadius: 1,
        backgroundColor: 'rgba(25,118,210,0.10)', color: 'primary.main', border: '1px solid', borderColor: 'rgba(25,118,210,0.30)',
        fontWeight: 500, fontSize: '0.8125rem', boxShadow: 2, cursor: 'grabbing',
    }}>
        <DragIndicatorIcon sx={{ fontSize: 16, color: 'primary.main', opacity: 0.6 }} />
        {label}
    </Box>
);
FieldChipVisual.propTypes = { label: PropTypes.node, dragging: PropTypes.bool };

// ─────────── helpers ───────────

const SettingRow = ({ label, help, control }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
        </Box>
        <Box sx={{ flexShrink: 0 }}>{control}</Box>
    </Box>
);

const SubBlock = ({ label, help, children }) => (
    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
        <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', display: 'block' }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{help}</Typography>}
        </Box>
        {children}
    </Paper>
);

const ColorTile = ({ label, value, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.65rem', letterSpacing: 0.5 }}>{label}</Typography>
        <Box component="label" sx={{
            position: 'relative', display: 'block',
            width: 36, height: 36, borderRadius: 1,
            border: '1px solid rgba(0,0,0,0.18)',
            backgroundColor: value, cursor: 'pointer',
        }}>
            <input type="color" value={value} onChange={(e) => onChange(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
        </Box>
    </Box>
);
const PercentInput = ({ label, value, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.65rem', letterSpacing: 0.5 }}>{label}</Typography>
        <TextField
            type="number" size="small" fullWidth
            value={value}
            onChange={(e) => onChange(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        />
    </Box>
);
const PixelInput = ({ label, value, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.65rem', letterSpacing: 0.5 }}>{label}</Typography>
        <TextField
            type="number" size="small" fullWidth
            value={value}
            onChange={(e) => onChange(Math.max(0, parseInt(e.target.value) || 0))}
            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
        />
    </Box>
);
const SelectField = ({ label, value, onChange, options }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.65rem', letterSpacing: 0.5 }}>{label}</Typography>
        <TextField
            select size="small" fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
        >
            {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
    </Box>
);

ShopTheLookTab.propTypes = { theme: PropTypes.string, t: PropTypes.func, config: PropTypes.object, onConfigChange: PropTypes.func, shopFieldLabels: PropTypes.object };
export default ShopTheLookTab;
